import { Injectable }   from '@angular/core';
import * as signalR     from '@microsoft/signalr';
import { SessionService } from './session.service';
import { environment }  from 'src/app/environments/environment';

@Injectable({ providedIn: 'root' })
export class SignalRService {

  private hubConnection!: signalR.HubConnection;

  // ✅ NEW — tracks an in-flight start() call so concurrent callers share
  // the same connection instead of each spinning up their own.
  private startPromise: Promise<void> | null = null;

  constructor(private sessionService: SessionService) {}

  startConnection(): Promise<void> {
    // Already connected — nothing to do.
    if (this.hubConnection &&
        this.hubConnection.state === signalR.HubConnectionState.Connected) {
      return Promise.resolve();
    }

    // A start is already in progress — reuse it rather than building a
    // second HubConnection object.
    if (this.startPromise) {
      return this.startPromise;
    }

    const hubUrl = `${environment.apiUrl}/hubs/tracking`;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () =>
          this.sessionService.getAccessToken() || ''
      })
      .withAutomaticReconnect()
      .build();

    this.hubConnection.onreconnected(() => {
      console.log('✅ SignalR Reconnected');
    });

    this.hubConnection.onclose(err => {
      console.warn('❌ SignalR Closed:', err);
    });

    this.startPromise = this.hubConnection.start()
      .then(() => {
        console.log('✅ SignalR Connected to /hubs/tracking');
      })
      .catch(err => {
        console.error('❌ SignalR Error:', err);
        // Allow a future call to retry from scratch.
        this.startPromise = null;
        throw err;
      });

    return this.startPromise;
  }

  // Generic event subscription — used by AiInsightsPanelComponent
  // for 'AiInsightCreated' and by other components for other events
  on(eventName: string, callback: (data: any) => void): void {
    if (this.hubConnection) {
      this.hubConnection.on(eventName, callback);
    }
  }

  onJobUpdated(callback: (data: any) => void): void {
    this.hubConnection.on('JobUpdated', callback);
  }

  onLogUpdate(callback: (data: any) => void): void {
    this.hubConnection.on('ReceiveLogUpdate', callback);
  }

  onLocationUpdate(callback: (data: any) => void): void {
    this.hubConnection.on('ReceiveLocationUpdate', callback);
  }

  stopConnection(): Promise<void> {
    this.startPromise = null;
    return this.hubConnection.stop();
  }
}